import React, { useState } from 'react';
import {
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Alert,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  VerifiedUser as VerifiedIcon,
  Schedule as PendingIcon,
  Refresh as RetryIcon
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';

// ============================================================================
// Types
// ============================================================================

type VerificationState = 'idle' | 'sending' | 'success' | 'error';

interface ChannelVerificationButtonProps {
  channelId: number;
  channelName: string;
  isVerified: boolean;
  onTest: (id: number) => Promise<{ success: boolean; message: string; deliveryTime?: number }>;
  onVerified?: (id: number) => void;
  variant?: 'button' | 'icon' | 'chip';
  size?: 'small' | 'medium';
  showSuccessDialog?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * ChannelVerificationButton - A button to send test notifications and verify channels.
 *
 * Features:
 * - Multiple display variants (button, icon, chip)
 * - Shows sending progress with visual feedback
 * - Success/failure dialog with details
 * - Displays delivery time on success
 * - Automatic retry option on failure
 */
export const ChannelVerificationButton: React.FC<ChannelVerificationButtonProps> = ({
  channelId,
  channelName,
  isVerified,
  onTest,
  onVerified,
  variant = 'button',
  size = 'small',
  showSuccessDialog = true
}) => {
  const theme = useTheme();

  // State
  const [state, setState] = useState<VerificationState>('idle');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    deliveryTime?: number;
  } | null>(null);

  // Handle test/verify action
  const handleTest = async () => {
    setState('sending');
    setResult(null);

    try {
      const testResult = await onTest(channelId);
      setResult(testResult);

      if (testResult.success) {
        setState('success');
        if (showSuccessDialog) {
          setDialogOpen(true);
        }
        // Notify parent of successful verification
        if (!isVerified && onVerified) {
          onVerified(channelId);
        }
      } else {
        setState('error');
        setDialogOpen(true);
      }
    } catch (err) {
      console.error('Channel test failed:', err);
      setResult({
        success: false,
        message: err instanceof Error ? err.message : 'Test failed'
      });
      setState('error');
      setDialogOpen(true);
    }
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setDialogOpen(false);
    // Reset state after a delay so user sees the success state briefly
    setTimeout(() => {
      if (state !== 'error') {
        setState('idle');
      }
    }, 300);
  };

  // Handle retry
  const handleRetry = () => {
    setDialogOpen(false);
    setState('idle');
    // Small delay before retrying
    setTimeout(handleTest, 100);
  };

  // Get button content based on state
  const getButtonContent = () => {
    switch (state) {
      case 'sending':
        return (
          <>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Sending...
          </>
        );
      case 'success':
        return (
          <>
            <CheckCircleIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Sent!
          </>
        );
      case 'error':
        return (
          <>
            <ErrorIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Failed
          </>
        );
      default:
        return (
          <>
            <SendIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Send Test
          </>
        );
    }
  };

  // Get button color based on state
  const getButtonColor = (): 'primary' | 'success' | 'error' | 'inherit' => {
    switch (state) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'primary';
    }
  };

  // Render based on variant
  const renderButton = () => {
    switch (variant) {
      case 'icon':
        return (
          <Tooltip
            title={
              state === 'sending'
                ? 'Sending test...'
                : 'Send test notification'
            }
          >
            <span>
              <IconButton
                size={size}
                color={getButtonColor()}
                onClick={handleTest}
                disabled={state === 'sending'}
              >
                {state === 'sending' ? (
                  <CircularProgress size={20} />
                ) : state === 'success' ? (
                  <CheckCircleIcon />
                ) : state === 'error' ? (
                  <ErrorIcon />
                ) : (
                  <SendIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        );

      case 'chip':
        return (
          <Chip
            icon={
              state === 'sending' ? (
                <CircularProgress size={14} />
              ) : state === 'success' ? (
                <CheckCircleIcon sx={{ fontSize: 16 }} />
              ) : state === 'error' ? (
                <ErrorIcon sx={{ fontSize: 16 }} />
              ) : isVerified ? (
                <VerifiedIcon sx={{ fontSize: 16 }} />
              ) : (
                <PendingIcon sx={{ fontSize: 16 }} />
              )
            }
            label={
              state === 'sending'
                ? 'Sending...'
                : state === 'success'
                ? 'Verified'
                : state === 'error'
                ? 'Failed'
                : isVerified
                ? 'Verified'
                : 'Unverified'
            }
            color={
              state === 'success' || isVerified
                ? 'success'
                : state === 'error'
                ? 'error'
                : 'warning'
            }
            variant={isVerified && state === 'idle' ? 'outlined' : 'filled'}
            size={size}
            onClick={handleTest}
            disabled={state === 'sending'}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                bgcolor:
                  state === 'success' || isVerified
                    ? alpha(theme.palette.success.main, 0.2)
                    : alpha(theme.palette.warning.main, 0.2)
              }
            }}
          />
        );

      default:
        return (
          <Button
            size={size}
            variant={isVerified ? 'outlined' : 'contained'}
            color={getButtonColor()}
            onClick={handleTest}
            disabled={state === 'sending'}
            startIcon={
              state === 'sending' ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              minWidth: 90,
              textTransform: 'none'
            }}
          >
            {getButtonContent()}
          </Button>
        );
    }
  };

  return (
    <>
      {renderButton()}

      {/* Result Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {result?.success ? (
              <CheckCircleIcon
                sx={{ mr: 1, color: theme.palette.success.main }}
              />
            ) : (
              <ErrorIcon sx={{ mr: 1, color: theme.palette.error.main }} />
            )}
            {result?.success ? 'Test Successful' : 'Test Failed'}
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {result?.success ? (
              <>
                <CheckCircleIcon
                  sx={{
                    fontSize: 64,
                    color: theme.palette.success.main,
                    mb: 2
                  }}
                />
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Test notification sent successfully to:
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  {channelName}
                </Typography>
                {result.deliveryTime && (
                  <Chip
                    label={`Delivered in ${result.deliveryTime}ms`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                )}
                <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                  Check your notification destination to confirm receipt.
                </Alert>
              </>
            ) : (
              <>
                <ErrorIcon
                  sx={{
                    fontSize: 64,
                    color: theme.palette.error.main,
                    mb: 2
                  }}
                />
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Failed to send test notification
                </Typography>
                <Alert severity="error" sx={{ textAlign: 'left' }}>
                  {result?.message || 'Unknown error occurred'}
                </Alert>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 2 }}
                >
                  Please check your channel configuration and try again.
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {result?.success ? (
            <Button onClick={handleCloseDialog} variant="contained">
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button
                onClick={handleRetry}
                variant="contained"
                startIcon={<RetryIcon />}
              >
                Retry
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChannelVerificationButton;
